import { PolicyMode } from '@kirin/api-client/contracts/policies';
import type { AllowBlockPolicyUiConfig } from './types';

export const ALLOW_BLOCK_POLICY_KEYS = [
  'mcp-server-sources',
  'extensions-management',
] as const;

const PUBLISHER_COLUMN_TOOLTIP =
  'Publishers may be specified by name or Git repository URL, with wildcards supported (For example - http://github.com/myorg/* )';

export function isAllowBlockPolicyKey(
  key: string
): key is import('./types').AllowBlockPolicyKey {
  return key === 'mcp-server-sources' || key === 'extensions-management';
}

export const MCP_ALLOW_BLOCK_CONFIG: AllowBlockPolicyUiConfig = {
  policyKey: 'mcp-server-sources',
  columns: [
    {
      id: 'mcpNameOrId',
      headerLabel: 'MCP Server Name',
      placeholder: 'Name or wildcard',
      kind: 'text',
    },
    {
      id: 'publisher',
      headerLabel: 'Publisher',
      headerTooltip: PUBLISHER_COLUMN_TOOLTIP,
      placeholder: 'Name, URL, or wildcard',
      kind: 'text',
    },
    {
      id: 'transport',
      headerLabel: 'Type',
      placeholder: 'Any',
      kind: 'select',
      options: [
        { value: 'any', label: 'Any' },
        { value: 'remote', label: 'Remote' },
        { value: 'local', label: 'Local' },
      ],
    },
  ],
  allowedSectionTitle: 'Allowed MCP Servers',
  blockedSectionTitle: 'Blocked MCP Servers',
  allowedNote:
    'MCP servers on the Allow list are always permitted, regardless of Block list entries or detection policies.',
  blockedNote:
    'Block list entries take precedence over detection policy restrictions but are overridden by the Allow list. Localhost HTTP MCP servers are included — a matching Block rule in Enforce will strip those configs. Allow-list a local server to keep it.',
  allowedSectionTooltip:
    'List of explicitly Allowed MCP servers - Fill in one or more columns, the values between the columns will be aggregated in "and" mode.',
  blockedSectionTooltip:
    'List of explicitly Blocked MCP servers - Fill in one or more columns, the values between the columns will be aggregated in "and" mode.',
  footerAndLogicText:
    'Empty fields match all values in that column. Field columns are combined using AND logic.',
};

export const EXTENSIONS_ALLOW_BLOCK_CONFIG: AllowBlockPolicyUiConfig = {
  policyKey: 'extensions-management',
  columns: [
    {
      id: 'extensionId',
      headerLabel: 'Extension Name',
      placeholder: 'ID, name, or wildcard',
      kind: 'text',
    },
    {
      id: 'version',
      headerLabel: 'Version',
      placeholder: '^1.0.0, ~2.1.0, >=3.0.0, *',
      kind: 'text',
    },
    {
      id: 'publisher',
      headerLabel: 'Publisher',
      placeholder: 'Publisher name or *',
      kind: 'text',
    },
  ],
  allowedSectionTitle: 'Allowed Extensions',
  blockedSectionTitle: 'Blocked Extensions',
  allowedNote:
    'Extensions on the Allow list are always permitted, regardless of Block list entries or detection policies.',
  blockedNote:
    'Block list entries take precedence over detection policy restrictions but are overridden by the Allow list.',
  allowedSectionTooltip:
    'List of explicitly Allowed Extensions - Fill in one or more columns, the values between the columns will be aggregated in "and" mode.',
  blockedSectionTooltip:
    'List of explicitly Blocked Extensions - Fill in one or more columns, the values between the columns will be aggregated in "and" mode.',
  footerAndLogicText:
    'Empty fields match all values in that column. Field columns are combined using AND logic.',
};

export const ALLOW_BLOCK_UI_CONFIG: Record<
  import('./types').AllowBlockPolicyKey,
  AllowBlockPolicyUiConfig
> = {
  'mcp-server-sources': MCP_ALLOW_BLOCK_CONFIG,
  'extensions-management': EXTENSIONS_ALLOW_BLOCK_CONFIG,
};

export function getAllowBlockUiConfig(
  policyKey: import('./types').AllowBlockPolicyKey
): AllowBlockPolicyUiConfig {
  return ALLOW_BLOCK_UI_CONFIG[policyKey];
}

export function defaultAllowBlockState(): import('./types').AllowBlockState {
  return {
    allowed: [],
    blocked: [],
    blockedListMode: PolicyMode.Monitor,
  };
}
